import React, { useMemo, useEffect, useRef } from "react";
import { Container, Text } from "@pixi/react";
import { TextStyle, TextMetrics } from "pixi.js";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { formatToCustomLocale } from "../../../../utilities";
import { setToolbarEdge } from "./toolbarEdges";
import { PixiSettings } from "../../../../pixiSettings";
import {
  FONT_CONFIG,
  USER_INTERFACE_BUTTON_CONFIG,
} from "../../../../pixiSettings";
import { outlineShadow } from "../../../../pixiSettings";
import {
  selectDisplayedBalance,
  selectCurrency,
  selectReplaySessionActive,
  selectReplayBalance,
} from "../../../../State/selectors";

// The portrait balance's amount: at most this share of the toolbar width.
const PORTRAIT_BALANCE_MAX_SHARE = 0.36;

const BalanceDisplay = ({ toolbar }) => {
  const { t } = useTranslation();
  const { device, orientation, getResponsiveConfig, uiSymbolScale, layout } =
    useDimensions();
  const displayedBalance = useSelector(selectDisplayedBalance);
  const currency = useSelector(selectCurrency);
  const replaySessionActive = useSelector(selectReplaySessionActive);
  const replayBalance = useSelector(selectReplayBalance);

  // While a replay round is active, show its own recorded wallet snapshot
  // instead of the live balance (docs/replay-history-spec.md §9). state.game
  // balance itself is never touched by replay, so this falls back to the
  // real live value the instant sessionActive goes false again — no
  // separate "restore" step needed on exit.
  const effectiveBalance =
    replaySessionActive && replayBalance != null
      ? replayBalance
      : displayedBalance;

  // Track previous balance to detect changes
  const prevBalanceRef = useRef(displayedBalance);

  // Log every balance change
  useEffect(() => {
    if (prevBalanceRef.current !== displayedBalance) {
      prevBalanceRef.current = displayedBalance;
    }
  }, [displayedBalance]);

  // Get button dimensions using the same system as PopiClock - memoized to prevent recalculation
  const buttonConfig = useMemo(
    () => getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );

  // Get unified balance configuration (font multiplier, y position) - memoized
  // X position now comes from toolbar.layout
  const balanceConfig = useMemo(() => {
    let fontMultiplier = 1.0;
    let valueScale = 1.0;   // Scale applied to value font size (relative to idealFontSize)
    let y = 0;

    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        // fontMultiplier = 1.25;
        // A tiny bit larger on an iPad upright (layout TABLET_PORTRAIT on
        // the MOBILE device, 2026-10-29).
        // fontMultiplier = layout === "TABLET_PORTRAIT" ? 1.3 : 1.25;
        // fontMultiplier = layout === "TABLET_PORTRAIT" ? 1.4 : 1.25;
        // fontMultiplier = layout === "TABLET_PORTRAIT" ? 1.45 : 1.25;
        fontMultiplier = layout === "TABLET_PORTRAIT" ? 1.4 : 1.25;
        // valueScale = 0.975;
        // The bet's size on an iPad upright: 0.975 snapped a full 2 px step
        // under the bet value there (2026-10-29).
        valueScale = layout === "TABLET_PORTRAIT" ? 1.0 : 0.975;
        y = -toolbar.visualHeight / 2;
      } else {
        fontMultiplier = 1.1;
        y = toolbar.height / 2;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        fontMultiplier = 1.5;
        y = -toolbar.visualHeight / 2;
      } else {
        fontMultiplier = 1.25;
        y = toolbar.height / 2;
      }
    } else {
      // Desktop
      fontMultiplier = 1.0;
      y = toolbar.height / 2;
    }

    return { fontMultiplier, valueScale, y };
  }, [device, orientation, layout, toolbar.height, toolbar.visualHeight]);

  // Calculate font sizes - memoized
  // Round to nearest even number for clean rasterization across DPR ratios
  const baseFontSize = 32;
  const idealFontSize = useMemo(() => {
    const responsiveFontSize = baseFontSize * balanceConfig.fontMultiplier;
    const raw = responsiveFontSize * uiSymbolScale * balanceConfig.valueScale;
    return Math.round(raw / 2) * 2; // Snap to even
  }, [balanceConfig.fontMultiplier, balanceConfig.valueScale, uiSymbolScale]);

  const LABEL_FONT_SIZE = useMemo(
    () => Math.round((idealFontSize / balanceConfig.valueScale * 0.9) / 2) * 2,
    [idealFontSize, balanceConfig.valueScale],
  );

  // Text style for balance label - memoized to prevent creating new TextStyle on every render
  const balanceLabelStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: LABEL_FONT_SIZE,
        fontWeight: "500",
        fill: 0xffffff,
        align: "left",
        resolution: window.devicePixelRatio || 1,
        // ...TEXT_STYLE_CONFIG.OUTLINE,
        ...outlineShadow(LABEL_FONT_SIZE), // shadow scales with the text
      }),
    [LABEL_FONT_SIZE],
  );

  // Format balance - memoized
  const formattedBalance = useMemo(
    () => formatToCustomLocale(effectiveBalance),
    [effectiveBalance],
  );

  // Prepare text content - memoized
  const balanceLabelText = useMemo(() => t("balance"), [t]);
  const balanceAmountText = useMemo(
    () => `${formattedBalance} ${currency}`,
    [formattedBalance, currency],
  );

  // Calculate maximum width based on 7-digit balance to stabilize component width
  // Use ideal font size for this calculation
  const maxBalanceWidth = useMemo(() => {
    const maxBalance = 9999999; // 7 digits
    const maxBalanceText = `${formatToCustomLocale(maxBalance)} ${currency}`;
    const tempStyle = new TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: idealFontSize,
      fontWeight: "700",
    });
    const maxBalanceMetrics = TextMetrics.measureText(
      maxBalanceText,
      tempStyle,
    );

    // return maxBalanceMetrics.width;
    // ── Portrait: at most this share of the toolbar (2026-10-15) ── a long
    // balance ("814.6410 mBTC") reached under the total win in the middle;
    // it now shrinks a little instead (the win fits the rest — WinDisplay).
    const portrait =
      (device === PixiSettings.DEVICE.MOBILE || device === PixiSettings.DEVICE.TABLET) &&
      orientation === PixiSettings.ORIENTATION.PORTRAIT;
    return portrait && toolbar.width > 0
      ? Math.min(maxBalanceMetrics.width, toolbar.width * PORTRAIT_BALANCE_MAX_SHARE)
      : maxBalanceMetrics.width;
  }, [currency, idealFontSize, device, orientation, toolbar.width]);

  /**
   * Auto-scaling font size calculation for balance amount
   * Similar to PopiBetButton: measures actual text and scales down if it exceeds max width
   * Only scales the amount, label stays fixed
   */
  const finalAmountFontSize = useMemo(() => {
    // Measure actual balance text with ideal font size
    const tempStyle = new TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: idealFontSize,
      fontWeight: "700",
    });
    const metrics = TextMetrics.measureText(balanceAmountText, tempStyle);

    // If text exceeds max width, scale down proportionally
    if (metrics.width > maxBalanceWidth) {
      const scale = maxBalanceWidth / metrics.width;
      return Math.round((idealFontSize * scale) / 2) * 2; // Snap to even
    }

    // Return ideal font size if it fits
    return idealFontSize;
  }, [balanceAmountText, idealFontSize, maxBalanceWidth]);

  const balanceAmountStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: finalAmountFontSize,
        fontWeight: "700",
        fill: 0xffffff,
        align: "left",
        resolution: window.devicePixelRatio || 1,
        // ...TEXT_STYLE_CONFIG.OUTLINE,
        ...outlineShadow(finalAmountFontSize), // shadow scales with the text
      }),
    [finalAmountFontSize],
  );

  // Measure text dimensions for positioning - KEEPS CENTERED LABEL FEATURE - memoized
  const textMetrics = useMemo(() => {
    const labelMetrics = TextMetrics.measureText(
      balanceLabelText,
      balanceLabelStyle,
    );
    const amountMetrics = TextMetrics.measureText(
      balanceAmountText,
      balanceAmountStyle,
    );

    const totalHeight = (labelMetrics.height + amountMetrics.height) * 0.95; // Reduce gap by 5%
    const maxWidth = Math.max(labelMetrics.width, maxBalanceWidth);
    const labelY = Math.round(-totalHeight / 2 + labelMetrics.height / 2);
    const amountY = Math.round(totalHeight / 2 - amountMetrics.height / 2);
    const textX = Math.round(maxWidth / 2);

    return {
      labelY,
      amountY,
      textX,
      drawnWidth: Math.max(labelMetrics.width, amountMetrics.width),
    };
  }, [
    balanceLabelText,
    balanceAmountText,
    balanceLabelStyle,
    balanceAmountStyle,
    maxBalanceWidth,
  ]);

  // Left-align text on mobile/tablet portrait
  const isPortraitMobile =
    (device === PixiSettings.DEVICE.MOBILE || device === PixiSettings.DEVICE.TABLET) &&
    orientation === PixiSettings.ORIENTATION.PORTRAIT;

  // Use layout position from toolbar hook if available - memoized
  const positionX = useMemo(
    () => toolbar.layout?.balance?.x ?? buttonConfig.width * 0.5,
    [toolbar.layout?.balance?.x, buttonConfig.width],
  );

  // Its text's right edge, for the win in the middle (toolbarEdges).
  useEffect(() => {
    setToolbarEdge("balanceRight", isPortraitMobile ? positionX + textMetrics.drawnWidth : null);
  }, [isPortraitMobile, positionX, textMetrics.drawnWidth]);
  useEffect(() => () => setToolbarEdge("balanceRight", null), []);

  return (
    <Container
      name="secondary_balance_display"
      x={positionX}
      y={balanceConfig.y}
      zIndex={5}
    >
      <Text
        key={`balance-label-column`}
        text={balanceLabelText}
        style={balanceLabelStyle}
        anchor={{ x: isPortraitMobile ? 0 : 0.5, y: 0.5 }}
        x={isPortraitMobile ? 0 : textMetrics.textX}
        y={textMetrics.labelY}
        roundPixels={true}
      />
      <Text
        key={`balance-amount-column`}
        text={balanceAmountText}
        style={balanceAmountStyle}
        anchor={{ x: isPortraitMobile ? 0 : 0.5, y: 0.5 }}
        x={isPortraitMobile ? 0 : textMetrics.textX}
        y={textMetrics.amountY}
        roundPixels={true}
      />
    </Container>
  );
};

export default BalanceDisplay;
